import { ReactNode } from 'react';
import { Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/layout/DashboardLayout';

/**
 * Route-level permission gate. Wrap a page element to block direct-URL access
 * for roles that lack the module — the sidebar already hides the link, this
 * stops someone typing the URL. Admins bypass. While permissions are still
 * loading we render nothing (avoids a flash-redirect). Denied users get an
 * inline "no access" card (not a redirect — that could loop if they also lack
 * the destination's permission).
 */
export function RequirePermission({ module, children }: { module: string; children: ReactNode }) {
  const { user, permissions, permissionsLoaded } = useAuth();
  const isAdmin = user?.role === 'admin';

  if (isAdmin) return <>{children}</>;
  if (!permissionsLoaded) return null; // perms in flight — decide once they land
  if (permissions?.[module]?.view === true) return <>{children}</>;

  return (
    <DashboardLayout title="No access">
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100">
          <Lock className="h-6 w-6 text-slate-400" />
        </div>
        <h2 className="text-lg font-semibold text-slate-800">You don't have access to this page</h2>
        <p className="mt-1 max-w-sm text-sm text-slate-500">
          Your role doesn't include the <span className="font-medium">{module}</span> module.
          Ask an administrator if you need access.
        </p>
      </div>
    </DashboardLayout>
  );
}
